import { useEffect, useState } from 'react';

interface PollOption {
  id: string;
  text: string;
  votes: number;
  percentage: number;
}

interface PollResults {
  pollId: string;
  options: PollOption[];
  totalVotes: number;
  winningOptionId: string | null;
  isActive: boolean;
}

interface PollResultsDisplayProps {
  pollId: string;
}

function PollResultsDisplay({ pollId }: PollResultsDisplayProps) {
  const [results, setResults] = useState<PollResults | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await fetch(`/api/polls/${pollId}/results`);
        if (response.ok) {
          const data = await response.json();
          setResults(data);
        }
      } catch (error) {
        console.error('[PollResultsDisplay] Error fetching results:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
    // Refresh ogni 2 secondi
    const interval = setInterval(fetchResults, 2000);
    return () => clearInterval(interval);
  }, [pollId]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center display-dark-bg">
        <div className="animate-spin rounded-full h-32 w-32 border-b-8 border-parchment-300"></div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="h-screen w-screen flex items-center justify-center display-dark-bg">
        <p className="text-3xl text-red-400">Risultati non disponibili</p>
      </div>
    );
  }

  // Ordina per percentuale decrescente
  const sortedOptions = [...results.options].sort((a, b) => b.percentage - a.percentage);
  const count = sortedOptions.length;
  const isCompact = count >= 5;

  return (
    <div className="h-screen w-screen flex flex-col items-center p-8 display-dark-bg">
      {/* Titolo */}
      <h1 className={`font-bold text-parchment-100 font-display shrink-0 ${isCompact ? 'text-4xl mb-4' : 'text-6xl mb-10'}`}>Risultati</h1>

      {/* Risultati */}
      <div className={`w-full max-w-5xl flex-1 flex flex-col justify-center ${isCompact ? 'gap-3' : 'gap-6'}`}>
        {sortedOptions.map((option) => {
          const isWinner = option.id === results.winningOptionId;

          return (
            <div
              key={option.id}
              className={`relative rounded-2xl border-2 transition-all ${isCompact ? 'p-3' : 'p-6'} ${
                isWinner
                  ? 'bg-yellow-500/20 border-yellow-400 ring-4 ring-yellow-400/50'
                  : 'display-panel'
              }`}
            >
              <div className={`flex items-center justify-between ${isCompact ? 'mb-2' : 'mb-4'}`}>
                <p className={`font-bold ${isCompact ? 'text-2xl' : 'text-3xl'} ${isWinner ? 'text-yellow-300' : 'text-parchment-100'}`}>
                  {option.text}
                </p>
                <p className={`font-bold ${isCompact ? 'text-3xl' : 'text-5xl'} ${isWinner ? 'text-yellow-300' : 'text-parchment-100'}`}>
                  {option.percentage}%
                </p>
              </div>

              {/* Progress bar */}
              <div className={`w-full bg-parchment-500/30 rounded-full overflow-hidden ${isCompact ? 'h-4' : 'h-6'}`}>
                <div
                  className={`h-full transition-all duration-1000 rounded-full ${
                    isWinner ? 'bg-yellow-400' : 'bg-parchment-300'
                  }`}
                  style={{ width: `${option.percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PollResultsDisplay;
